<template>
  <div>
    <navigation-back class="lg:mb-5" label="Back" route="service-dashboard" v-if="isVerifiedDirector"/>
    <div class="container mx-auto mt-7 mb-7">
      <div class="mx-4">
        <div class="md:flex md:flex-row md:items-center py-px">
          <div class="w-full md:w-3/4 mb-4 md:mb-0">
            <doe-page-heading
              :label="`Educators Dashboard for ${serviceSeNumber}`"
            />
          </div>
          <div class="w-full md:w-1/4">
            <p class="md:text-right">
              <doe-button
                v-if="hasDirectorRights || isVerifiedDirector"
                appearance="primary"
                size="small"
                suffix="plus"
                iconSize="small"
                @click="newUser()"
              >
                Add educator
              </doe-button>
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="container mx-auto">
      <div class="mx-4">
        <div class="border-l-2 border-red-900 py-2 px-2 md:px-4 max-w-4xl mb-6">
          <h3 class="block font-sans font-semibold leading-snug max-w-4xl subpixel-antialiased text-2xl text-blue-900 lg:leading-snug">Important Information</h3>
          <p class="font-sans text-blue-900 mt-2 text-base">Please note there may be a short delay in newly added educators appearing on this dashboard. If your added educator doesn’t appear within 15 minutes and after refreshing your screen please contact the department’s Information and Enquiries team on 1800 619 113 (toll free) or at <a href="mailto:information@earlylearningcommission.nsw.gov.au" class="underline">information@earlylearningcommission.nsw.gov.au</a>.</p>
        </div>
        <div v-if="rows.length">
          <doe-table
            dataset="User"
            sortKey="educatorsFullName"
            :actions="actions"
            :columns="columns"
            :settings="settings"
            :rows="rows"
            :hasActionBoxes="false"
            :isSearchable="true"
            @action:delete="actionDelete($event)"
          />
        </div>

        <div v-else class="border-t border-gray-500 pt-7">
          <h2
            class="block font-sans font-semibold leading-snug max-w-4xl mb-4 subpixel-antialiased text-3xl text-blue-900"
          >
            Welcome to your educators dashboard
          </h2>
          <p
            class="font-sans font-light max-w-4xl text-base text-black leading-loose mb-4"
          >
           On this page, you can add or remove access to Educators associated with this service.
          </p>
          <ul class="list-disc mb-5 pl-4">
            <li
              class="font-sans font-light max-w-4xl text-base text-black leading-loose mb-2"
            >
             Adding Educators will enable them access to the Self-Assessment and Quality Improvement Planning Portal.
            </li>
            <li
              class="font-sans font-light max-w-4xl text-base text-black leading-loose mb-2"
            >
              Removing Administrators or Educators will cancel their access to the dashboard for this service, but will not remove access to other service/s they may be linked to and will not remove their Department of Education account.
            </li>
            <li
              class="font-sans font-light max-w-4xl text-base text-black leading-loose"
            >
              See our
              <a
                href="/assets/Self Assessment and Quality Improvement Planning Portal User Guide.pdf"
                class="underline cursor-pointer">User Guide<span class="sr-only">Opens external website</span></a
              >
              for more information on Administrator responsibilities associated with managing access to the Self-Assessment and Quality Improvement Planning Portal.
            </li>
          </ul>
          <doe-button
            v-if="hasDirectorRights || isVerifiedDirector"
            class="mt-6"
            appearance="primary"
            size="large"
            suffix="plus"
            iconSize="medium"
            @click="newUser()"
          >
            Add educator
          </doe-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { serviceCallWithAuth } from "@/helpers/cognito";
import handleRoles from "@/mixins/handleRoles";
import { DASHBOARD } from "@/data/navigation";
import { COLUMNS, ACTIONS, SETTINGS } from "@/data/userTable";
import NavigationBack from "@/components/navigation/NavigationBack";
import DoePageHeading from "@/components/typography/PageHeading";
import DoeTable from "@/components/table/Table";
import DoeButton from "@/components/button/Button";

export default {
  name: "users",
  mixins: [handleRoles],
  components: {
    NavigationBack,
    DoePageHeading,
    DoeTable,
    DoeButton
  },
  data() {
    return {
      navigation: DASHBOARD,
      columns: COLUMNS,
      actions: ACTIONS,
      settings: SETTINGS,
      q: "",
      users: [],
      deletableIndex: false
    };
  },
  mounted() {
    this.$root.isLoading = true;
    let headers = this.$OAuth.buildHttpHeader();
    let options = {
      path: `/users/${this.detuserguid}`,
      headers: headers
    };
    serviceCallWithAuth("doe-ttss", options)
      .then(result => {
        this.users = result.body;
      })
      .catch(reason => {
        if (reason.message) {
          this.$root.translateModal(reason);
        } else {
          this.$root.translateModal(reason, "Error");
        }
      })
      .finally(() => {
        this.$root.isLoading = false;
      });
  },
  computed: {
    ...mapGetters(["getService", "authUser", "idToken"]),
    rows() {
      return this.users.filter(user =>
        Object.keys(user.entitlements).includes(this.serviceSeNumber)
      );
    },
    profile() {
      return this.$OAuth.data.profile;
    },
    detuserguid() {
      return this.profile.detuserguid
        ? this.profile.detuserguid
        : "";
    },
    serviceData() {
      return this.getService;
    },
    serviceName() {
      return this.serviceData.serviceTradingAsName;
    },
    serviceSeNumber () {
      return this.serviceData.seNumber;
    }
  },
  methods: {
    newUser() {
      this.$router.push({ name: "user-create" });
    },
    actionDelete(deletable) {
      // console.log(deletable);
      const seNumber = this.serviceSeNumber;
      const detUserGuid = this.profile.detuserguid;
      const serviceRole = this.getServiceRole;
      this.deletableIndex = this.users.findIndex(
        user => user.id === deletable.id
      );
      const userId = this.users[this.deletableIndex].userName;
      this.$root.translateModal(
        "Please confirm by clicking the Continue button below.",
        "Ready to remove user?",
        {
          cancellable: true,
          action: () => {
            this.$root.isLoading = true;

            let body = {
              op: "remove",
              seNumber: this.getService.seNumber,
              role: deletable.role,
              detUserGuid: deletable.id
            };
            let headers = this.$OAuth.buildHttpHeader();
            let options = {
              path: `/users/${deletable.id}`,
              headers: headers,
              method: "PATCH",
              body: JSON.stringify(body)
            };
            serviceCallWithAuth("doe-esam", options)
              .then(() => {
                this.users.splice(this.deletableIndex, 1);
              })
              .catch(reason => {
                if (reason.message) {
                  this.$root.translateModal(reason.message);
                } else {
                  this.$root.translateModal(reason, "Error");
                }
              })
              .finally(() => {
                this.$root.isLoading = false;
              });
          }
        }
      );
    }
  }
};
</script>

<style lang="scss" scoped>
li /deep/ a {
  @apply inline-block relative underline;
}
li /deep/ a[href*="//"]:not([href*="localhost.com"]) {
  padding-right: 1.5rem;
  &:after {
    @apply absolute block;
    content: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 100 89'><path d='M100,4.16666667 L100,26.3861111 C100,30.1119792 95.4930556,31.9381944 92.8869792,29.3324653 L86.6878472,23.1329861 L44.4095486,65.4112847 C42.7822917,67.0385417 40.1442708,67.0385417 38.5170139,65.4112847 L34.5887153,61.4829861 C32.9614583,59.8557292 32.9614583,57.2175347 34.5887153,55.5904514 L76.8673611,13.3118056 L70.6689236,7.11302083 C68.0524306,4.49652778 69.9055556,0 73.6152778,0 L95.8333333,0 C98.1345486,0 100,1.86545139 100,4.16666667 Z M70.6647569,47.0128472 L67.8869792,49.790625 C67.1055707,50.5720468 66.6666073,51.6318913 66.6666667,52.7369792 L66.6666667,77.7777778 L11.1111111,77.7777778 L11.1111111,22.2222222 L56.9444444,22.2222222 C58.049457,22.2221592 59.1092025,21.783211 59.890625,21.0019097 L62.6684028,18.2241319 C65.2932292,15.5991319 63.4342014,11.1111111 59.7222222,11.1111111 L8.33333333,11.1111111 C3.73090278,11.1111111 0,14.8420139 0,19.4444444 L0,80.5555556 C0,85.1579861 3.73090278,88.8888889 8.33333333,88.8888889 L69.4444444,88.8888889 C74.046875,88.8888889 77.7777778,85.1579861 77.7777778,80.5555556 L77.7777778,49.9590278 C77.7777778,46.246875 73.2895833,44.3878472 70.6647569,47.0128472 Z' /></svg>");
    top: 0px;
    right: 0px;
    width: 1.25rem;
    height: 1.25rem;
  }
}
</style>
